import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CalendarCheck, Clock, MapPin, Medal, Users } from 'lucide-react';
import { courtDisplayName } from '../../engine';
import { useLocale, useT } from '../../i18n';
import {
  fetchMyActivities,
  fetchMyBookings,
  fetchMyMatches,
  type MyHistoryActivity,
  type MyHistoryBooking,
  type MyHistoryMatch,
} from '../../lib/api/myHistory';
import { localMs, relativeDayLabel, splitUpcomingPast } from './memberData';
import { Card, EmptyState } from './mobileUi';

// ─────────────────────────────────────────────────────────────────────
// «ALLE KLUBBER» — the person's own history across every club (#760).
//
// A segment beside «Kommende» and «Tidligere» in Reservasjoner, and beside
// «Kommende» and «Spilt» in Kamper. The club segments are unchanged and still
// read the ACTIVE club; this reads the account-scoped `/me/*` endpoints, which
// answer with the person's own rows from every club, each labelled with the
// club it belongs to.
//
// Read only when the segment is opened, not when the tab is: most visits never
// ask, and a tab should not cost three requests nobody looked at.
//
// What it shows is exactly what the server projects — the person's own
// bookings, their own registrations, their own matches with the others named.
// No court nicknames (those live in the club's configuration, which this does
// not read) and no payment button (paying is done inside the club, where the
// booking and its price are the club's own).
// ─────────────────────────────────────────────────────────────────────

type Loaded<R> = { status: 'loading' } | { status: 'failed' } | { status: 'ready'; data: R };

/**
 * Load once per mount and per retry. `load` answers null when ANY read went
 * unanswered — a half-loaded history would read as «you have less than you
 * do», so it is a failure with a retry instead.
 */
function useHistory<R>(load: () => Promise<R | null>) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Loaded<R>>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void load().then((data) => {
      if (cancelled) return;
      setState(data == null ? { status: 'failed' } : { status: 'ready', data });
    });
    return () => {
      cancelled = true;
    };
    // `load` is one of the two module-level loaders below, so it never changes.
  }, [attempt, load]);
  return { state, retry: () => setAttempt((a) => a + 1) };
}

function Lead({ children }: { children: ReactNode }) {
  return <p className="text-[12px] text-ink-3 px-1 -mt-1">{children}</p>;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 px-1 pt-1 @wide:col-span-2">
      {children}
    </div>
  );
}

function ClubTag({ name }: { name: string }) {
  return (
    <span
      className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 truncate max-w-[45%]"
      style={{ background: '#eef0ea', color: '#0f1611' }}
      data-testid="history-club"
    >
      {name}
    </span>
  );
}

function Loading() {
  const t = useT();
  return (
    <div className="px-1 py-6 text-center text-[13px] text-ink-3" role="status">
      {t('mobile.history.loading')}
    </div>
  );
}

function Failed({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="rounded-2xl px-4 py-6 text-center" style={{ background: '#fff' }}>
      <div className="text-[13px] font-semibold text-ink-2">{t('mobile.history.failed')}</div>
      <button type="button" className="btn-ghost text-xs mt-2" onClick={onRetry}>
        {t('mobile.history.retry')}
      </button>
    </div>
  );
}

const timeRange = (start: string | null, end: string | null) =>
  start ? (end ? `${start}–${end}` : start) : '';

type Item =
  | { kind: 'booking'; key: string; startMs: number; endMs: number; b: MyHistoryBooking }
  | { kind: 'activity'; key: string; startMs: number; endMs: number; a: MyHistoryActivity };

async function loadReservations() {
  const [bookings, activities] = await Promise.all([fetchMyBookings(), fetchMyActivities()]);
  if (bookings.failed || activities.failed) return null;
  return { bookings: bookings.items, activities: activities.items };
}

/** Reservasjoner › Alle klubber: own bookings and sign-ups, in every club. */
export function AcrossClubsReservations() {
  const t = useT();
  const { state, retry } = useHistory(loadReservations);

  const { upcoming, past } = useMemo(() => {
    const { bookings, activities } =
      state.status === 'ready' ? state.data : { bookings: [], activities: [] };
    const items: Item[] = [
      ...bookings.map((b) => ({
        kind: 'booking' as const,
        // A booking id is unique within ITS club only.
        key: `b:${b.club.slug}:${b.id}`,
        startMs: localMs(b.date, b.startTime),
        endMs: localMs(b.date, b.endTime || b.startTime),
        b,
      })),
      ...activities
        .filter((a) => a.when.date)
        .map((a) => ({
          kind: 'activity' as const,
          key: `a:${a.club.slug}:${a.id}`,
          startMs: localMs(a.when.date!, a.when.start),
          endMs: localMs(a.when.date!, a.when.end || a.when.start),
          a,
        })),
    ];
    return splitUpcomingPast(items);
  }, [state]);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'failed') return <Failed onRetry={retry} />;
  return (
    <div className="space-y-2" data-testid="across-clubs-reservations">
      <Lead>{t('mobile.history.lead')}</Lead>
      {upcoming.length + past.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck size={22} />}
          title={t('mobile.history.empty')}
          hint={t('mobile.history.emptyHint')}
        />
      ) : (
        <div className="space-y-2 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-2 @wide:items-start">
          {upcoming.length > 0 && <SectionLabel>{t('mobile.reservations.upcoming')}</SectionLabel>}
          {upcoming.map((it) => (
            <ReservationRow key={it.key} it={it} upcoming />
          ))}
          {past.length > 0 && <SectionLabel>{t('mobile.reservations.past')}</SectionLabel>}
          {past.map((it) => (
            <ReservationRow key={it.key} it={it} upcoming={false} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReservationRow({ it, upcoming }: { it: Item; upcoming: boolean }) {
  const t = useT();
  if (it.kind === 'booking') {
    const b = it.b;
    return (
      <Card accent="#c2f02f">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
            {relativeDayLabel(b.date)} · {timeRange(b.startTime, b.endTime)}
          </span>
          <ClubTag name={b.club.name} />
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
            <Clock size={14} className="text-ink-3" />
            {b.title || t('mobile.reservations.courtBooking')}
          </div>
          <span className="font-mono text-[10px] text-ink-2 flex items-center gap-1 flex-shrink-0">
            <MapPin size={11} /> {courtDisplayName(b.court, {})}
          </span>
        </div>
        {upcoming && b.paymentStatus === 'pending' && (
          <div className="mt-1 text-[12px] text-ink-2">{t('mobile.reservations.unpaid')}</div>
        )}
      </Card>
    );
  }
  const a = it.a;
  const waitlist = a.registration.status === 'waitlist';
  return (
    <Card accent="#84cc16">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
          {relativeDayLabel(a.when.date!)}
          {a.when.days > 1 ? ` (${t('mobile.history.days', { count: a.when.days })})` : ''}
          {a.when.start ? ` · ${timeRange(a.when.start, a.when.end)}` : ''}
        </span>
        <ClubTag name={a.club.name} />
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
          <Users size={14} className="text-ink-3" /> {a.name || t('mobile.common.activity')}
        </div>
        <span
          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: waitlist ? '#fef9c3' : '#f7fdde', color: '#0f1611' }}
        >
          {waitlist ? t('mobile.common.waitlist') : t('mobile.common.signedUp')}
        </span>
      </div>
      {a.registration.asPartner && (
        <div className="mt-1 text-[12px] text-ink-2">{t('mobile.history.asPartner')}</div>
      )}
    </Card>
  );
}

async function loadMatches() {
  const matches = await fetchMyMatches();
  return matches.failed ? null : matches.items;
}

/** Kamper › Alle klubber: own single matches, in every club, newest first. */
export function AcrossClubsMatches() {
  const t = useT();
  const { state, retry } = useHistory(loadMatches);
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'failed') return <Failed onRetry={retry} />;
  const matches = state.data;
  return (
    <div className="space-y-2" data-testid="across-clubs-matches">
      <Lead>{t('mobile.history.matchesLead')}</Lead>
      {matches.length === 0 ? (
        <EmptyState
          icon={<Medal size={22} />}
          title={t('mobile.history.emptyMatches')}
          hint={t('mobile.history.emptyMatchesHint')}
        />
      ) : (
        <div className="space-y-2 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-2 @wide:items-start">
          {matches.map((m) => (
            <MatchRow key={`${m.club.slug}:${m.id}`} m={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function MatchRow({ m }: { m: MyHistoryMatch }) {
  const { t, formatDate } = useLocale();
  const won = m.won;
  const us = m.partner?.name
    ? t('mobile.history.youAnd', { name: m.partner.name })
    : t('mobile.history.you');
  const them = m.opponents
    .map((o) => o.name)
    .filter(Boolean)
    .join(' & ');
  return (
    <Card accent={won == null ? '#8a948c' : won ? '#84cc16' : '#f43f5e'}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
          {t('mobile.reservations.singleMatch')}
          {m.playedAt
            ? ` · ${formatDate(new Date(m.playedAt), { day: 'numeric', month: 'short' })}`
            : ''}
        </span>
        <span
          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
          style={{
            background: won == null ? '#eef0ea' : won ? '#f7fdde' : '#ffe4e6',
            color: won == null ? '#4a544c' : won ? '#3f6212' : '#be123c',
          }}
        >
          {won == null
            ? t('mobile.matches.playedTag')
            : won
              ? t('mobile.matches.wonTag')
              : t('mobile.matches.lostTag')}
        </span>
      </div>
      {/* Two lines rather than three columns: at phone width a name beside the
          score beside two more names truncates all of them. */}
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="text-[13px] font-semibold text-ink truncate">{us}</div>
        <div className="font-mono font-bold text-[14px] text-ink tabular-nums flex-shrink-0">
          {m.sets.map((s) => `${s.mine}-${s.theirs}`).join('  ')}
        </div>
      </div>
      {them && (
        <div className="text-[13px] text-ink-2 truncate">
          {t('mobile.history.versus', { names: them })}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <ClubTag name={m.club.name} />
        {m.eloChange != null && (
          <span
            className="text-[11px] font-mono font-bold flex-shrink-0"
            style={{ color: m.eloChange >= 0 ? '#3f6212' : '#be123c' }}
          >
            {m.eloChange >= 0 ? '+' : ''}
            {Math.round(m.eloChange)} ELO
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Reservasjoner or Kamper with NO club chosen (#760, owner decision
 * 2026-11-02): «a player with no club sees the cross-club list» — not «Velg en
 * klubb». The person's own history is theirs whichever club is in context, so
 * the tab answers with it, and choosing a club stays one tap away for the
 * club's own segments.
 *
 * Only for an account that has a person to ask about; `MobileApp` decides
 * that, as it does for the «Alle klubber» segment.
 */
export function AcrossClubsWithoutClub({
  tab,
  onChooseClub,
}: {
  tab: 'reservations' | 'matches';
  onChooseClub: () => void;
}) {
  const t = useT();
  return (
    <div className="px-4 pb-4 space-y-3" data-testid="across-clubs-without-club">
      <h1
        className="font-display font-semibold text-ink leading-tight pt-2"
        style={{ fontSize: 26 }}
      >
        {tab === 'reservations' ? t('mobile.reservations.title') : t('mobile.common.matches')}
      </h1>
      <div
        className="rounded-2xl px-4 py-3 flex items-center justify-between gap-3"
        style={{ background: '#fff' }}
      >
        <p className="text-[12px] text-ink-3">{t('mobile.history.noClubLead')}</p>
        <button type="button" className="btn-ghost text-xs flex-shrink-0" onClick={onChooseClub}>
          {t('mobile.history.chooseClub')}
        </button>
      </div>
      {tab === 'reservations' ? <AcrossClubsReservations /> : <AcrossClubsMatches />}
    </div>
  );
}
